import { useEffect, useMemo, useRef } from "react";
import { OrderTypeResponse, SideResponse } from "../../../codegen-api";
import { FormValidatorKeysEnum } from "../../../enums/form";
import { leverageCap, resolveMaxLeverage } from "../../../utils/leverage";
import { PerpFormFieldKeyEnum } from "./form";
import { useShortcut, useKeyboardScope } from "keyboard/useKeyboard";
import { usePerpsTradeFormSetup } from "./usePerpsTradeFormSetup";
import { useResetOnMarketSwitch } from "./form.useResetOnMarketSwitch";
import { reduceOnlyState } from "./reduceOnlyState";

export function usePerpsTradeFormEffects(
  ctx: ReturnType<typeof usePerpsTradeFormSetup>
) {
  const {
    setOrderDirection,
    setOrderType,
    form,
    setReduceOnly,
    unregister,
    errors,
    defaultValues,
    setValue,
    isDirty,
    trigger,
    reduceOnly,
    orderDirection,
    amount,
    price,
    currentPosition,
    positionsKnown,
    isPerp,
    accountData,
    perpInstrument,
    account,
    leverage,
    leverageFieldDirty,
    resetField,
    tpsl,
  } = ctx;

  // --- Keyboard shortcuts ("trade" scope). Config chords set the order form;
  // the action chords (leverage / close / submit) click the real buttons so they
  // respect validation, the disabled state and any confirmation modal. ---
  useKeyboardScope("trade");
  useShortcut("trade.buy", () => setOrderDirection(SideResponse.Buy));
  useShortcut("trade.sell", () => setOrderDirection(SideResponse.Sell));
  useShortcut("trade.market", () => setOrderType(OrderTypeResponse.Market));
  useShortcut("trade.limit", () => setOrderType(OrderTypeResponse.Limit));
  useShortcut("trade.size", () => form.setFocus(PerpFormFieldKeyEnum.AMOUNT));
  useShortcut("trade.price", () => {
    setOrderType(OrderTypeResponse.Limit);
    setTimeout(() => form.setFocus(PerpFormFieldKeyEnum.PRICE), 60);
  });
  // Perp-only actions pass the key through (`false`) on a spot form: there is no
  // reduce-only, position to close or leverage there (A11). Hooks cannot be
  // registered conditionally, so the handler declines instead.
  useShortcut("trade.reduceOnly", () => {
    if (!isPerp) return false;
    setReduceOnly((v) => !v);
    return true;
  });
  // Jump into the order-entry section from anywhere (no mouse): scroll the form
  // into view and focus the size field, the start of placing an order.
  useShortcut("trade.focusForm", () => {
    document
      .querySelector("[data-trade-form]")
      ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    form.setFocus(PerpFormFieldKeyEnum.AMOUNT);
  });
  const cycleOrderType = () =>
    setOrderType((tp) => (tp === OrderTypeResponse.Market ? OrderTypeResponse.Limit : OrderTypeResponse.Market));
  useShortcut("trade.cycleLeft", cycleOrderType);
  useShortcut("trade.cycleRight", cycleOrderType);

  // Click a real order-form button by its data-attribute; returns false (pass
  // through) when the control is absent or disabled, so nothing fires blindly.
  const clickTradeControl = (selector: string) => {
    const el = document.querySelector<HTMLButtonElement>(selector);
    if (!el || el.disabled) return false;
    el.click();
    return true;
  };
  useShortcut("trade.leverage", () =>
    isPerp ? clickTradeControl("[data-trade-leverage]") : false
  );
  useShortcut("trade.closePosition", () =>
    isPerp ? clickTradeControl("[data-trade-close]") : false
  );
  useShortcut("trade.submit", () => clickTradeControl("[data-trade-submit]"));

  // On unmount, unregister from form
  useEffect(
    () => () => {
      Object.values(PerpFormFieldKeyEnum).forEach((k) => {
        unregister(k);
      });
    },
    [unregister]
  );

  const orderValueTooSmall =
    errors?.amount?.type === FormValidatorKeysEnum.orderValueTooSmall;

  const notEnoughBalanceError =
    errors?.amount?.type === FormValidatorKeysEnum.notEnoughBalance;

  // Updates default values
  useEffect(() => {
    if (defaultValues?.amount) {
      setValue(PerpFormFieldKeyEnum.AMOUNT, defaultValues.amount, {
        shouldValidate: true,
      });
    }
  }, [defaultValues, setValue]);

  // Market triggers

  // Update whenever reduce only checkbox is changed
  useEffect(() => {
    if (isDirty) {
      trigger(PerpFormFieldKeyEnum.AMOUNT);
    }
  }, [reduceOnly, orderDirection, trigger, isDirty]);

  // Limit triggers

  useEffect(() => {
    if (isDirty && amount && price) {
      trigger("price");
      trigger("amount");
    }
  }, [amount, price, trigger, isDirty]);

  useEffect(() => {
    if (isDirty && price) {
      trigger("price");
    }
  }, [orderDirection, price, trigger, isDirty]);

  // Reduce only is disabled if theres no position, or position === orderDirection.
  // "No position" is claimed only once positions are KNOWN (B-2); before that
  // the order waits (submit stays disabled) with a neutral note instead.
  const {
    notAllowed: reduceOnlyNotAllowed,
    positionsUnknown: reduceOnlyPositionsUnknown,
  } = useMemo(
    () => reduceOnlyState(currentPosition, orderDirection, Boolean(positionsKnown)),
    [currentPosition, orderDirection, positionsKnown]
  );

  const accountLeverage = useMemo(
    () =>
      accountData?.leverages?.find(
        (lev) =>
          lev.instrument_id?.toLowerCase() ===
          perpInstrument?.instrument_id?.toLowerCase()
      )?.leverage,
    [accountData?.leverages, perpInstrument?.instrument_id]
  );

  // Unknown max leverage (no `max_leverage` from /markets) is NOT guessed from
  // a client table: the cap becomes the account's current leverage, so the
  // form cannot move it, and the leverage controls say it is unavailable.
  const knownMaxLeverage = useMemo(
    () => resolveMaxLeverage(perpInstrument),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [perpInstrument?.max_leverage]
  );
  const maxLeverage = useMemo(
    () => leverageCap(knownMaxLeverage, accountLeverage),
    [knownMaxLeverage, accountLeverage]
  );

  const selectedLeverage = useMemo(() => {
    const lev = Number(leverage || accountLeverage || 1);
    if (!Number.isFinite(lev) || lev <= 0) return 1;
    return Math.max(1, Math.min(Math.round(lev), Math.floor(maxLeverage)));
  }, [accountLeverage, leverage, maxLeverage]);

  // An order composed for one market is meaningless in the next one, and this form
  // is reused across a switch rather than remounted. See the hook for the repro.
  useResetOnMarketSwitch(perpInstrument?.instrument_id, () => {
    setValue(PerpFormFieldKeyEnum.PRICE, "", { shouldValidate: false });
    setValue(PerpFormFieldKeyEnum.TRIGGER_PRICE, "", { shouldValidate: false });
    setValue(PerpFormFieldKeyEnum.AMOUNT, "", { shouldValidate: false });
    tpsl.reset();
  });

  const leverageDefaultsKeyRef = useRef<string>();
  useEffect(() => {
    if (!perpInstrument?.instrument_id) return;

    const defaultLeverage = Number(accountLeverage || 1);
    const normalizedDefault = Math.max(
      1,
      Math.min(
        Math.round(Number.isFinite(defaultLeverage) ? defaultLeverage : 1),
        Math.floor(maxLeverage)
      )
    );
    const nextLeverage = normalizedDefault.toFixed(2);
    const leverageDefaultsKey = `${account || ""}:${
      perpInstrument.instrument_id
    }`;
    const instrumentChanged =
      leverageDefaultsKeyRef.current !== leverageDefaultsKey;

    if (instrumentChanged) {
      leverageDefaultsKeyRef.current = leverageDefaultsKey;
      resetField(PerpFormFieldKeyEnum.LEVERAGE, {
        defaultValue: nextLeverage,
      });
      return;
    }

    if (leverageFieldDirty) return;

    const currentLeverage = Number(leverage);
    if (
      Number.isFinite(currentLeverage) &&
      Math.round(currentLeverage) === normalizedDefault
    ) {
      return;
    }

    setValue(PerpFormFieldKeyEnum.LEVERAGE, nextLeverage, {
      shouldValidate: true,
    });
  }, [
    account,
    accountLeverage,
    leverage,
    leverageFieldDirty,
    maxLeverage,
    perpInstrument?.instrument_id,
    resetField,
    setValue,
  ]);

  return {
    ...ctx,
    orderValueTooSmall,
    notEnoughBalanceError,
    reduceOnlyNotAllowed,
    reduceOnlyPositionsUnknown,
    accountLeverage,
    maxLeverage,
    maxLeverageKnown: knownMaxLeverage !== undefined,
    selectedLeverage,
  };
}
